import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';

import { Logger } from '@farmersdog/logger';

import { useGetLead } from '../../graphql/queries/useGetLead';

import { useVetCustomizedPlan } from './useVetCustomizedPlan';

interface useCheckVetRecipesDiscrepancyArgs {
  vetSelectedRecipeNames: string[];
  availableRecipeNames: string[] | undefined;
}

export const useCheckVetRecipesDiscrepancy = ({
  vetSelectedRecipeNames,
  availableRecipeNames,
}: useCheckVetRecipesDiscrepancyArgs): void => {
  const { data } = useGetLead();
  const vetCustomizedPlan = useVetCustomizedPlan();
  const vetReferredDogName = vetCustomizedPlan?.dogName;
  const leadEmail = data?.lead?.email;
  const leadSelectedRecipeNames = useMemo(
    () =>
      data?.lead?.pets?.find(pet => pet.name === vetReferredDogName)?.selection
        ?.fresh?.options.recipes ?? [],
    [data?.lead?.pets, vetReferredDogName]
  );

  const logger = useMemo(
    () => new Logger('tosa:frontend:vet:checkVetRecipesDiscrepancy'),
    []
  );

  useEffect(() => {
    if (
      availableRecipeNames &&
      vetSelectedRecipeNames.length > 0 &&
      leadEmail
    ) {
      const unavailableRecipes = vetSelectedRecipeNames.filter(
        recipe => !availableRecipeNames.includes(recipe)
      );

      if (unavailableRecipes.length > 0) {
        logger.error('Vet plan recipe not found in available recipes', {
          vetSelectedRecipeNames,
          availableRecipeNames,
          leadEmail,
        });
      }
    }
  }, [vetSelectedRecipeNames, availableRecipeNames, leadEmail, logger]);

  useEffect(() => {
    if (
      leadSelectedRecipeNames.length > 0 &&
      vetSelectedRecipeNames.length > 0
    ) {
      const arePetAndVetSelectionEqual = isEqual(
        leadSelectedRecipeNames.toSorted(),
        vetSelectedRecipeNames.toSorted()
      );

      if (!arePetAndVetSelectionEqual) {
        logger.error('Fresh selection on pet does not match vet selection', {
          vetSelectedRecipeNames,
          leadSelectedRecipeNames,
          leadEmail,
        });
      }
    }
  }, [vetSelectedRecipeNames, leadSelectedRecipeNames, leadEmail, logger]);
};
